import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Sefaria from './sefaria/sefaria';
import SearchAnalytics, { resultLinkAnalyticsAttrs } from './sefaria/searchAnalytics';
import { InterfaceText } from './Misc';
import BreadcrumbPath from './BreadcrumbPath';

// How long the finger has to stay put before the card lights up. Short enough to feel
// immediate on a real tap, long enough that a scroll flick never highlights anything.
const PRESS_DELAY_MS = 100;
// A tap shorter than PRESS_DELAY_MS still has to show feedback, so once the pressed state
// goes on screen it stays there at least this long.
const PRESS_MIN_MS = 150;
// Finger drift, in pixels, still counted as a tap rather than the start of a scroll.
const PRESS_MOVE_TOLERANCE = 10;

// Tracks whether a touch gesture is a tap (finger stays put) so the card shows its pressed
// state only for taps, never for a scroll that happens to start on a card.
function usePressState() {
  const ref = useRef(null);
  const [pressed, setPressed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let startPos  = null;   // where the finger landed; null once the gesture ends or is ruled out
    let showTimer = null;   // fires PRESS_DELAY_MS after touchstart
    let hideTimer = null;   // holds a quick tap's highlight on screen for PRESS_MIN_MS
    let shownAt   = null;   // when the pressed state actually went on screen

    const clearTimers = () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      showTimer = hideTimer = null;
    };
    const show = () => { shownAt = Date.now(); setPressed(true); };
    const hide = () => { shownAt = null; setPressed(false); };

    const onStart = (e) => {
      const t = e.touches[0];
      clearTimers();
      hide();
      startPos = { x: t.clientX, y: t.clientY };
      showTimer = setTimeout(show, PRESS_DELAY_MS);
    };
    const onMove = (e) => {
      if (!startPos) return;
      const t = e.touches[0];
      if (Math.abs(t.clientX - startPos.x) > PRESS_MOVE_TOLERANCE ||
          Math.abs(t.clientY - startPos.y) > PRESS_MOVE_TOLERANCE) {
        // The finger is scrolling, not tapping — abandon the gesture without ever showing it.
        startPos = null;
        clearTimers();
        hide();
      }
    };
    const onEnd = () => {
      if (!startPos) return;   // already ruled out as a scroll
      startPos = null;
      clearTimers();
      if (shownAt === null) { show(); }   // finger lifted before the delay: flash it now
      const remaining = PRESS_MIN_MS - (Date.now() - shownAt);
      if (remaining > 0) { hideTimer = setTimeout(hide, remaining); } else { hide(); }
    };
    const onCancel = () => {
      startPos = null;
      clearTimers();
      hide();
    };

    el.addEventListener('touchstart',  onStart,  { passive: true });
    el.addEventListener('touchmove',   onMove,   { passive: true });
    el.addEventListener('touchend',    onEnd,    { passive: true });
    el.addEventListener('touchcancel', onCancel, { passive: true });
    return () => {
      clearTimers();
      el.removeEventListener('touchstart',  onStart);
      el.removeEventListener('touchmove',   onMove);
      el.removeEventListener('touchend',    onEnd);
      el.removeEventListener('touchcancel', onCancel);
    };
  }, []);

  return [ref, pressed];
}

const TYPE_ICONS = {
  author:     '/static/icons/iconmonstr-pen-17.svg',
  topic:      '/static/icons/topic.svg',
  text:       '/static/icons/book.svg',
  collection: '/static/icons/collection.svg',
};

const MODES_WITH_CATEGORY_COLOR = new Set(['books', 'sources']);

// Singular and plural are separate interface strings so each language can inflect its own way.
const versionsToggleLabel = (count) => Sefaria._bilingual(
  count === 1 ? 'search.result_card.one_more_version' : 'search.result_card.more_versions',
  { count }
);

function SearchResultCard({
  mode,
  name,
  hebrewName,
  altName,
  hebrewAltName,
  crumbs,
  secondaryDate,
  hebrewSecondaryDate,
  secondaryAuthor,
  hebrewSecondaryAuthor,
  secondaryAuthorHref,
  secondaryAuthorAlt,
  hebrewSecondaryAuthorAlt,
  description,
  hebrewDescription,
  descriptionHtml,
  icon,
  type = 'text',
  href,
  tref,
  onResultClick,
  openURL,
  query,
  accentColor,
  analyticsPosition,
  // Sources-mode specific props
  snippet,
  snippetLang,
  versionName,
  hebrewVersionName,
  versions,
  currVersions,
  textHighlights,
}) {
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [pressRef, isPressed] = usePressState();

  // Reports a click anywhere on this card as a search_element_clicked (type 'result') GA4
  // event, and — for clicks that navigate the current window — ends the search flow with
  // reason 'clicked_result'. Must fire from the card itself: entity cards navigate via
  // window.location.href and every link here stops propagation, so none of these paths
  // reaches ReaderApp's central link handler. Only cards given an analyticsPosition (the
  // main search page) report; compare-panel and sidebar-search cards stay silent.
  //
  // `elementValue` names what was clicked — the result's ref for the card and its title,
  // the category or author name for the links inside it.
  const fireResultClickAnalytics = (elementValue, endsFlow = true) => {
    if (analyticsPosition) {
      SearchAnalytics.resultClicked(elementValue, analyticsPosition, endsFlow);
    }
  };

  // Stamped on every link in the card so that a modified click — which ReaderApp kills at
  // the document capture phase, before any React handler runs — can still be attributed to
  // this result. See SearchAnalytics.reportModifiedResultLinkClick.
  const linkAnalyticsAttrs = (elementValue) => resultLinkAnalyticsAttrs(elementValue, analyticsPosition);

  // The single click path for every clickable part of the card. Opening a result has to
  // carry two things the ref alone can't: which version matched, and which words to
  // highlight once the panel renders. `target` says *which* result was clicked — the
  // card's own version, or one of the rows in the expanded versions list.
  const openResult = async (target) => {
    Sefaria.track.event('Search', 'Search Result Card Click', `${query} - ${name}`);
    if (!onResultClick) {
      // Books/authors/topics results have no ref-based handler; they navigate by URL instead.
      // openURL is ReaderApp's router entry point, so this opens a panel in place of reloading
      // the page. It returns false for a path it doesn't recognize — then fall back to a real
      // page load, which is also what happens outside the app (e.g. Storybook), where the prop
      // isn't supplied at all.
      if (openURL && openURL(target.href)) { return; }
      window.location.href = target.href;
      return;
    }
    let ref = target.tref ?? target.href;
    if (target.tref) {
      // If a title was renamed since the last Elasticsearch reindex, there's no local
      // index entry for this ref, so ask the API to normalize it
      // (e.g. "Bereishit Rabbah 3" => "Bereshit Rabbah 3") before handing it to the reader.
      const parsedRef = Sefaria.parseRef(target.tref);
      ref = parsedRef.index?.length ? parsedRef.ref : (await Sefaria.getRef(target.tref)).ref;
    }
    onResultClick(
      ref,
      target.currVersions ?? null,
      target.textHighlights?.length ? { textHighlights: target.textHighlights } : null
    );
  };

  // The card's own result — the version named in searchResultCard-versionName.
  const ownResult = { tref, href, currVersions, textHighlights };

  const handleCardClick = () => {
    if (window.getSelection && window.getSelection().toString()) return;
    fireResultClickAnalytics(tref ?? name);
    openResult(ownResult);
  };

  // Links inside the card keep a real href so Cmd/Ctrl-click still opens a new tab, but a
  // plain click runs the in-app handler — otherwise different parts of one card behave
  // differently depending on where you click.
  const handleLinkClick = (target) => (e) => {
    e.stopPropagation();  // the card's own onClick would fire too, opening the wrong version
    // A modified click opens a new tab or window and leaves the user on the search page, so
    // it reports the click but must not end the flow. Inside ReaderApp these clicks never
    // get here at all (ReaderApp reports them itself, see linkAnalyticsAttrs above); the
    // check keeps the card correct on its own, e.g. in Storybook.
    const opensNewTab = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
    fireResultClickAnalytics(tref ?? name, !opensNewTab);
    // Modified clicks, and cards with no in-app handler at all, fall through to the browser;
    // the href already points at the matched version with the query highlighted.
    if ((!onResultClick && !openURL) || opensNewTab) return;
    e.preventDefault();
    openResult(target);
  };

  // Links to somewhere other than the result itself (the author under a book title, the
  // breadcrumb categories). They never open a result, so they don't go through openResult —
  // they just need the same "don't reload the page" treatment as the title link.
  //
  // These links lead away from search in the current window, so each is a result click that
  // ends the flow. `elementValue` names what was clicked for analytics (the category or
  // author name); `linkHref` is where it goes. A modified click opens a new tab and leaves
  // the user on the search page, so it reports the click without ending the flow — the same
  // split handleLinkClick makes.
  const handleSubLinkClick = (elementValue, linkHref) => (e) => {
    e.stopPropagation();  // otherwise the card's onClick opens the result instead
    const opensNewTab = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
    fireResultClickAnalytics(elementValue, !opensNewTab);
    if (!openURL || opensNewTab) return;
    if (openURL(linkHref)) { e.preventDefault(); }
  };

  // Middle-click opens the link in a new tab natively. Browsers dispatch `auxclick` rather
  // than `click` for non-primary mouse buttons, so neither handleLinkClick nor
  // handleSubLinkClick above ever runs for this gesture -- this is its only chance to be
  // reported. Like a modified click it leaves the user on the search page, so it reports the
  // click WITHOUT ending the flow, and the browser is left alone to open the tab.
  const handleAuxClick = (elementValue) => (e) => {
    if (e.button !== 1) { return; }   // 1 == middle; back/forward buttons aren't link clicks
    fireResultClickAnalytics(elementValue, false);
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  // Books and sources derive their accent color from the Sefaria category palette.
  // An explicit accentColor prop always wins (useful for overrides / storybook).
  const resolvedAccentColor = accentColor
    || (MODES_WITH_CATEGORY_COLOR.has(mode) && name ? Sefaria.palette.refColor(name) : '#000');

  const isSourcesMode = mode === 'sources';
  // Sources cards don't show the icon circle — the colored bar and ref title are sufficient.
  const resolvedIcon = isSourcesMode ? null : (icon || TYPE_ICONS[type.toLowerCase()] || TYPE_ICONS.text);
  const hasDescription = !isSourcesMode && !!(descriptionHtml || description);

  return (
    <div
      ref={pressRef}
      className={`searchResultCard searchResultCard--${mode}${isPressed ? ' is-pressed' : ''}`}
      role="link"
      tabIndex={0}
      aria-label={name}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className="searchResultCard-bar" style={{ backgroundColor: resolvedAccentColor }} />
      <div className={`searchResultCard-content${hasDescription ? '' : ' searchResultCard-content--centered'}`}>
        {resolvedIcon && (
          <div className="searchResultCard-iconCircle">
            {(type === 'text' || type === 'collection') && MODES_WITH_CATEGORY_COLOR.has(mode) ? (
              <span
                className="searchResultCard-iconImg searchResultCard-iconImg--masked"
                role="img"
                aria-label={`${type} icon`}
                style={{
                  WebkitMaskImage: `url(${resolvedIcon})`,
                  maskImage: `url(${resolvedIcon})`,
                  backgroundColor: resolvedAccentColor,
                }}
              />
            ) : (
              <img
                src={resolvedIcon}
                alt={`${type} icon`}
                className={`searchResultCard-iconImg searchResultCard-iconImg--${type.toLowerCase()}`}
              />
            )}
          </div>
        )}
        <div className="searchResultCard-body">
          {crumbs && crumbs.length > 0 && (
            <BreadcrumbPath
              crumbs={crumbs}
              getCrumbLinkProps={(crumb) => ({
                ...linkAnalyticsAttrs(crumb.label),
                onClick: handleSubLinkClick(crumb.label, crumb.href),
                onAuxClick: handleAuxClick(crumb.label),
              })}
            />
          )}
          <div className="searchResultCard-header">
            <a href={href} className="searchResultCard-titleLink"
               {...linkAnalyticsAttrs(tref ?? name)}
               onClick={handleLinkClick(ownResult)}
               onAuxClick={handleAuxClick(tref ?? name)}>
              <div className="searchResultCard-titleRow">
                <span className="searchResultCard-name">
                  <InterfaceText text={{ en: name, he: hebrewName }} />
                </span>
                {altName && (
                  <span className="searchResultCard-altName">
                    <InterfaceText text={{ en: altName, he: hebrewAltName }} />
                  </span>
                )}
              </div>
            </a>
            {!isSourcesMode && (secondaryDate || secondaryAuthor) && (
              <div className="searchResultCard-secondary">
                {secondaryDate && (
                  <span className="searchResultCard-secondary-date">
                    <InterfaceText text={{ en: secondaryDate, he: hebrewSecondaryDate }} />
                  </span>
                )}
                {secondaryDate && secondaryAuthor && (
                  <span className="searchResultCard-secondary-dot" aria-hidden="true">·</span>
                )}
                {secondaryAuthor && (
                  <span className="searchResultCard-secondary-author">
                    {secondaryAuthorHref ? (
                      <a href={secondaryAuthorHref}
                         {...linkAnalyticsAttrs(secondaryAuthor)}
                         onClick={handleSubLinkClick(secondaryAuthor, secondaryAuthorHref)}
                         onAuxClick={handleAuxClick(secondaryAuthor)}>
                        <InterfaceText text={{ en: secondaryAuthor, he: hebrewSecondaryAuthor }} />
                      </a>
                    ) : (
                      <InterfaceText text={{ en: secondaryAuthor, he: hebrewSecondaryAuthor }} />
                    )}
                  </span>
                )}
                {secondaryAuthor && secondaryAuthorAlt && (
                  <span className="searchResultCard-secondary-authorAlt">
                    <InterfaceText text={{ en: secondaryAuthorAlt, he: hebrewSecondaryAuthorAlt }} />
                  </span>
                )}
              </div>
            )}
          </div>

          {isSourcesMode && snippet && (
            <div
              className={`searchResultCard-snippet${snippetLang === 'he' ? ' he' : ' en'}`}
              dangerouslySetInnerHTML={{ __html: snippet }}
            />
          )}

          {isSourcesMode && versionName && (
            <div className="searchResultCard-versionName">
              <InterfaceText text={{ en: versionName, he: hebrewVersionName || versionName }} />
            </div>
          )}

          {isSourcesMode && versions && versions.length > 0 && (
            <div className="searchResultCard-versionsSection">
              <button
                type="button"
                className="searchResultCard-versionsToggle"
                onClick={(e) => { e.stopPropagation(); setVersionsOpen(o => !o); }}
                aria-expanded={versionsOpen}
              >
                <InterfaceText text={versionsToggleLabel(versions.length)} />
                <span
                  className={`searchResultCard-versionsChevron${versionsOpen ? ' open' : ''}`}
                  aria-hidden="true"
                />
              </button>
              {versionsOpen && (
                <div className="searchResultCard-versionsList">
                  {versions.map((v, i) => (
                    <a
                      key={i}
                      href={v.href}
                      className="searchResultCard-versionItem"
                      {...linkAnalyticsAttrs(tref ?? name)}
                      onClick={handleLinkClick(v)}
                      onAuxClick={handleAuxClick(tref ?? name)}
                    >
                      <div
                        className={`searchResultCard-snippet${v.snippetLang === 'he' ? ' he' : ' en'}`}
                        dangerouslySetInnerHTML={{ __html: v.snippet }}
                      />
                      <div className="searchResultCard-versionName">
                        <InterfaceText text={{ en: v.versionName, he: v.hebrewVersionName || v.versionName }} />
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {!isSourcesMode && (descriptionHtml || description) && (
            <div className="searchResultCard-description">
              {descriptionHtml
                ? <span dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
                : <InterfaceText text={{ en: description, he: hebrewDescription }} />
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

SearchResultCard.propTypes = {
  mode:                 PropTypes.oneOf(['books', 'authors', 'topics', 'sources']).isRequired,
  name:                 PropTypes.string.isRequired,
  hebrewName:           PropTypes.string,
  altName:              PropTypes.string,
  hebrewAltName:        PropTypes.string,
  crumbs:               PropTypes.arrayOf(PropTypes.shape({
    label:       PropTypes.string.isRequired,
    hebrewLabel: PropTypes.string,
    href:        PropTypes.string,
  })),
  secondaryDate:           PropTypes.string,
  hebrewSecondaryDate:     PropTypes.string,
  secondaryAuthor:         PropTypes.string,
  hebrewSecondaryAuthor:   PropTypes.string,
  secondaryAuthorHref:     PropTypes.string,
  secondaryAuthorAlt:      PropTypes.string,
  hebrewSecondaryAuthorAlt: PropTypes.string,
  description:          PropTypes.string,
  hebrewDescription:    PropTypes.string,
  icon:                 PropTypes.string,
  type:                 PropTypes.oneOf(['author', 'topic', 'text', 'collection']),
  href:                 PropTypes.string.isRequired,
  tref:                 PropTypes.string,
  onResultClick:        PropTypes.func,
  // URL-based in-app navigation (ReaderApp.openURL), used by cards whose href isn't a ref:
  // books, categories, authors and topics. Returns true if it handled the path.
  openURL:              PropTypes.func,
  query:                PropTypes.string,
  accentColor:          PropTypes.string,   // explicit override; skips palette lookup
  analyticsPosition:    PropTypes.number,   // 1-based rank; presence opts the card into search analytics
  // Sources mode
  snippet:              PropTypes.string,
  snippetLang:          PropTypes.oneOf(['en', 'he']),
  versionName:          PropTypes.string,
  hebrewVersionName:    PropTypes.string,
  versions:             PropTypes.arrayOf(PropTypes.shape({
    snippet:          PropTypes.string.isRequired,
    snippetLang:      PropTypes.oneOf(['en', 'he']),
    versionName:      PropTypes.string,
    hebrewVersionName: PropTypes.string,
    href:             PropTypes.string,
    // Each row opens its own version, so it needs its own ref/version/highlights.
    tref:             PropTypes.string,
    currVersions:     PropTypes.object,
    textHighlights:   PropTypes.arrayOf(PropTypes.string),
  })),
  // The version that matched, keyed by reader slot: 'he' = primary text, 'en' = translation.
  currVersions:         PropTypes.shape({
    en: PropTypes.shape({ versionTitle: PropTypes.string, languageFamilyName: PropTypes.string }),
    he: PropTypes.shape({ versionTitle: PropTypes.string, languageFamilyName: PropTypes.string }),
  }),
  // Matched words, re-highlighted in the reader after the panel opens.
  textHighlights:       PropTypes.arrayOf(PropTypes.string),
};

export { BreadcrumbPath };
export default SearchResultCard;
